import { ReactNode, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import { InformationDrawerLayout } from "../../layer13";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import noResultsImage from "resources/images/no-results-x.png";
import { LargeCustomIcon, MediumCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import {
  EmployeeSummaryPrepCard,
  LastWeekDetailCard,
  PerformanceReviewCard,
  PrepSupportNeedCard,
  SentimentChangeCard,
} from "../../layer06";
import type { IChangeState } from "../../layer06/SentimentChangeCard/SentimentChangeCard";
import { SelectCheckInDateField } from "../../layer04";
import { ConversationStarterList } from "../../layer07";
import {
  selectAllCompanyUsersById,
  selectCurrentUserInfo,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import AvatarCircle from "app/components/AvatarCircle";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { useGetCheckInSummaryQuery } from "app/containers/TeamsTab/teamsCheckInApi";
import { useGetRollingCheckInSummaryQuery } from "app/containers/TeamsTab/teamsAiServiceApi";
import {
  generateEmployeeCheckInReviewInstructions,
  selectGenerateInstructionsStatus,
  sendGeneralMessage,
  setChatbotMessages,
} from "app/containers/Chatbot/slice";
import { getLetsChatIntroMessage } from "app/containers/Chatbot/helpers";
import {
  useNavigate,
  useStartChatAboutTopic,
  useStartNewChat,
} from "../../../common";
import { CompletedCheckInSummary } from "app/containers/TeamsTab/types";

type OneOnOnePrepModalProps = {
  id: string;
  isVisible: boolean;
  handleClose: () => void;
  userAccountId?: number | null;
  date?: string | null;
  mostRecentCompletedCheckIn?: boolean;
  perspective?: "manager" | "self";
};

function trendToChange(direction: string | null | undefined): IChangeState {
  if (direction === "up") return "INCREASE";
  if (direction === "down") return "DECREASE";
  return "NONE";
}

function trendSubtitle(direction: string | null | undefined): string {
  if (direction === "up") return "Improving from last week";
  if (direction === "down") return "Declining from last week";
  if (direction === "neutral") return "No change from last week";
  return "No prior week data";
}

const getWinBulletPoints = (checkInData: CompletedCheckInSummary | null) => {
  if (!checkInData?.winSpotlight) return null;
  return [
    {
      header: "Achievement",
      content: checkInData.winSpotlight.achievement,
    },
    { header: "Impact", content: checkInData.winSpotlight.impact },
    {
      header: "Recognition",
      content: checkInData.winSpotlight.recognition,
    },
  ];
};

const getIssueBulletPoints = (checkInData: CompletedCheckInSummary | null) => {
  if (!checkInData?.issueSpotlight) return null;
  return [
    { header: "Issue", content: checkInData.issueSpotlight.issue },
    { header: "Impact", content: checkInData.issueSpotlight.impact },
    { header: "Action", content: checkInData.issueSpotlight.action },
  ];
};

const getInsightBulletPoints = (
  checkInData: CompletedCheckInSummary | null
) => {
  if (!checkInData?.insights) return null;
  return [
    {
      header: "Development",
      content: checkInData?.insights?.development,
    },
  ];
};

type RollingSummaryData = ReturnType<
  typeof useGetRollingCheckInSummaryQuery
>["data"];

const getRollingSummary = (rollingData: RollingSummaryData) =>
  rollingData && "success" in rollingData && rollingData.success
    ? rollingData.data.overallSummary
    : null;

const isRollingNotEnoughData = (rollingData: RollingSummaryData) =>
  !!rollingData &&
  "hasEnoughData" in rollingData &&
  rollingData.hasEnoughData === false;

const formatDateTime = (input: string) => {
  // Replace 'T' with a space, then strip off any timezone offset (+00:00, -05:00, Z, etc.)
  return input
    .replace("T", " ")
    .replace(/([+-]\d{2}:\d{2}|Z)$/, "")
    .slice(0, 19);
};

const closestCheckInDate = (
  activeDate: string | undefined,
  checkInHistory: CompletedCheckInSummary["checkInHistory"] | undefined
): string | null => {
  if (!checkInHistory?.length) return null;
  if (!activeDate) return checkInHistory[0].scheduledDate;

  const sorted = [...checkInHistory].sort(
    (a, b) =>
      new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
  );

  const targetDateTime = new Date(formatDateTime(activeDate)).getTime();

  let result = sorted[0].scheduledDate;

  for (const item of sorted) {
    if (item.scheduledDate === activeDate) {
      return item.scheduledDate;
    }
    const itemDateTime = new Date(item.scheduledDate).getTime();
    if (itemDateTime > targetDateTime) {
      return result;
    }
    result = item.scheduledDate;
  }
  return result;
};

export const OneOnOnePrepModal = ({
  userAccountId = null,
  isVisible,
  handleClose,
  id,
  date,
  mostRecentCompletedCheckIn = false,
  perspective = "manager",
}: OneOnOnePrepModalProps) => {
  const isSelf = perspective === "self";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const startNewChat = useStartNewChat();
  // Self view reuses Bo Zone's "Let's talk" behavior.
  const { startChatAboutTopic, pendingTopic } = useStartChatAboutTopic();
  const [selectedDate, setSelectedDate] = useState<string | null | undefined>(
    date
  );
  const [pendingStarter, setPendingStarter] = useState<string | null>(null);
  const generateStatus = useAppSelector(selectGenerateInstructionsStatus);
  const isGenerating = generateStatus === "loading";
  const currentUserInfo = useAppSelector(selectCurrentUserInfo);

  // Sets up a Bo chat loaded with this employee's check-in and adds Bo's
  // intro message. Returns false (after showing a toast) when the chat
  // couldn't be set up.
  const setUpEmployeeCheckInChat = async (employeeId: number) => {
    const result = await dispatch(
      generateEmployeeCheckInReviewInstructions({
        userAccountId: employeeId,
        weeklyScope: 1,
      })
    );
    // On failure the previous thread is still active, so leave its messages alone.
    if (!generateEmployeeCheckInReviewInstructions.fulfilled.match(result)) {
      toast.error("Couldn't start a chat with Bo. Please try again.");
      return false;
    }
    dispatch(
      setChatbotMessages([
        {
          role: "assistant",
          content: getLetsChatIntroMessage(
            currentUserInfo?.firstName ?? "",
            displayName
          ),
        },
      ])
    );
    return true;
  };

  const handleLetsChat = async () => {
    if (isSelf) {
      await startNewChat();
      return;
    }
    if (!userAccountId) return;
    if (!(await setUpEmployeeCheckInChat(userAccountId))) return;
    navigate("/user/chatWithBo");
  };

  const handleStarterChat = async (starter: string) => {
    if (!userAccountId) return;
    setPendingStarter(starter);
    try {
      // Don't send the starter into a stale or unrelated thread.
      if (!(await setUpEmployeeCheckInChat(userAccountId))) return;
      dispatch(
        sendGeneralMessage({
          message: `Help me bring up “${starter}” with ${
            firstName || displayName
          } in our 1:1.`,
        })
      );
      navigate("/user/chatWithBo");
    } finally {
      setPendingStarter(null);
    }
  };

  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const companyUsersById = useAppSelector(selectAllCompanyUsersById);
  const userInfo =
    userAccountId !== null
      ? companyUsersById[userAccountId] ?? sampleUsersById[userAccountId]
      : undefined;
  const firstName = userInfo?.firstName ?? "";
  const displayName = userInfo
    ? `${userInfo.firstName} ${userInfo.lastName}`.trim()
    : "Team Member";

  const activeDate = selectedDate ?? undefined;

  const summaryQueryArgs =
    userAccountId !== null
      ? { userAccountId, date: activeDate, mostRecentCompletedCheckIn }
      : skipToken;

  const { data, isFetching, isError } =
    useGetCheckInSummaryQuery(summaryQueryArgs);

  const { data: rollingData, isFetching: isRollingFetching } =
    useGetRollingCheckInSummaryQuery(summaryQueryArgs);

  const rollingSummary = getRollingSummary(rollingData);
  const rollingNotEnoughData = isRollingNotEnoughData(rollingData);

  const checkInData = data?.checkInCompleted ? data : null;
  // e.g. a 404 from a deep link to someone outside the user's span of control.
  // Checked first because `data` can still hold the last person's summary.
  const showError = !isFetching && isError;
  const showEmpty = !isFetching && !isError && !!data && !checkInData;
  const showContent = !isFetching && !isError && !!checkInData;

  const resolvedScheduledDate = closestCheckInDate(
    activeDate ?? checkInData?.checkInCompletedDate,
    checkInData?.checkInHistory
  );

  const formattedCheckInDate = resolvedScheduledDate
    ? new Date(resolvedScheduledDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const winBulletPoints = getWinBulletPoints(checkInData);
  const issueBulletPoints = getIssueBulletPoints(checkInData);
  const insightBulletPoints = getInsightBulletPoints(checkInData);

  const getEmployeeSummaryPrepCard = () => {
    if (isRollingFetching) {
      return (
        <EmployeeSummaryPrepCard
          content=""
          energyTrend={checkInData?.energyTrend}
          moodTrend={checkInData?.trendDirection.mood}
          isSummaryLoading
        />
      );
    }

    if (rollingNotEnoughData && !rollingSummary) {
      return (
        <EmployeeSummaryPrepCard
          content="Not enough check-in data to generate a summary yet."
          energyTrend={checkInData?.energyTrend}
          moodTrend={checkInData?.trendDirection.mood}
        />
      );
    }

    return (
      <EmployeeSummaryPrepCard
        content={
          rollingSummary ||
          checkInData?.summary ||
          "No summary available for this check-in cycle."
        }
        energyTrend={checkInData?.energyTrend}
        moodTrend={checkInData?.trendDirection.mood}
      />
    );
  };

  const getLetsChatButtonIcon = () => {
    if (isGenerating) {
      return <CircularProgress size={16} color="inherit" />;
    }
    return <LargeCustomIcon icon="SPARKLE" />;
  };

  const getPerformanceInANutshellCard = () => {
    if (!winBulletPoints && !issueBulletPoints && !insightBulletPoints) {
      return null;
    }
    return (
      <>
        <Divider className="border-sapphire-05" />
        <ModalSection
          title={
            isSelf
              ? "Your Performance in a Nutshell"
              : `${firstName || "Team Member"}'s Performance in a Nutshell`
          }
        >
          <Stack rowGap="1rem">
            {winBulletPoints && (
              <PerformanceReviewCard
                cardType="WIN"
                bulletPoints={winBulletPoints}
              />
            )}
            {issueBulletPoints && (
              <PerformanceReviewCard
                cardType="ISSUE"
                bulletPoints={issueBulletPoints}
              />
            )}
            {insightBulletPoints && (
              <PerformanceReviewCard
                cardType="INSIGHTS"
                bulletPoints={insightBulletPoints}
              />
            )}
          </Stack>
        </ModalSection>
      </>
    );
  };

  const getWeekFocusSection = () => {
    if (!checkInData?.currentWeekWork) return null;
    return (
      <ModalSection title={null}>
        <Stack direction="column" rowGap="1.5rem">
          <Typography
            variant="body1"
            className="text-teamApp-text-tertiary"
            sx={{
              fontSize: ".875rem",
              lineHeight: "1.125rem",
            }}
          >
            {checkInData.currentWeekWork}
          </Typography>

          {checkInData.strengthsObserved?.primary ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              gap="1rem"
            >
              {checkInData.strengthsObserved?.primary ? (
                <LastWeekDetailCard
                  title="Strength Observed"
                  subTitle={checkInData.strengthsObserved.primary}
                />
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </ModalSection>
    );
  };

  const getNextWeekSection = () => {
    if (!checkInData?.nextWeekWork) return null;
    return (
      <>
        <Divider className="border-sapphire-05" />
        <ModalSection title="This Week">
          <Typography
            variant="body1"
            className="text-teamApp-text-tertiary"
            sx={{
              fontSize: ".875rem",
              lineHeight: "1.125rem",
            }}
          >
            {checkInData.nextWeekWork}
          </Typography>
        </ModalSection>
      </>
    );
  };

  const getConversationStarterSection = () => {
    if (!checkInData?.conversationsStarted?.length) return null;
    return (
      <>
        <Divider className="border-sapphire-05" />
        <ModalSection
          title={
            isSelf
              ? "Conversation Starters"
              : `Conversation Starters for ${firstName || "Team Member"}`
          }
        >
          <ConversationStarterList
            checkInSummary={checkInData}
            action={{
              label: "Chat with Bo about this",
              icon: <MediumCustomIcon icon="CHAT" />,
              onClick: isSelf ? startChatAboutTopic : handleStarterChat,
              pendingStarter: isSelf ? pendingTopic : pendingStarter,
              isDisabled: isGenerating,
            }}
          />
        </ModalSection>
      </>
    );
  };

  const getPreviousCheckInSentimentSection = () => {
    if (!showContent) {
      return null;
    }
    const isOpen = checkInData?.visibilityMode === "open";
    const keys = ["mood", "readiness", "progress"] as const;
    return keys.map((key) => {
      const title = key.charAt(0).toUpperCase() + key.slice(1);
      const direction = checkInData?.scoreTrends?.[key];
      return (
        <Box key={title} flex="1 1 0" minWidth={0}>
          <SentimentChangeCard
            title={title}
            subTitle={trendSubtitle(direction)}
            change={trendToChange(direction)}
            stateNumber={isOpen ? checkInData?.scores?.[key] : undefined}
          />
        </Box>
      );
    });
  };

  return (
    <InformationDrawerLayout
      id={id}
      isVisible={isVisible}
      handleClose={handleClose}
      title={isSelf ? "Your Check-in Recap" : `1:1 Prep for ${displayName}`}
      subTitle={
        formattedCheckInDate
          ? `Check-in summary for week of ${formattedCheckInDate}`
          : "Check-in summary"
      }
      headerActionButton={
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Button
            sx={{ height: "2.5rem" }}
            startIcon={getLetsChatButtonIcon()}
            variant="outlined"
            disabled={isGenerating}
            onClick={handleLetsChat}
          >
            Let&apos;s Chat
          </Button>
        </Box>
      }
      startIcon={
        userAccountId !== null ? (
          <AvatarCircle userAccountId={userAccountId} size="medium" />
        ) : (
          <Avatar sx={{ width: "2.5rem", height: "2.5rem" }} />
        )
      }
    >
      <Stack padding="2rem" rowGap="1.5rem">
        <ModalSection title={null}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            columnGap=".5rem"
            rowGap=".5rem"
            flexWrap="wrap"
          >
            <SelectCheckInDateField
              checkInHistory={
                data && "checkInHistory" in data
                  ? data.checkInHistory
                  : undefined
              }
              selectedDate={resolvedScheduledDate}
              onChange={(scheduledDate) => setSelectedDate(scheduledDate)}
            />
          </Stack>
        </ModalSection>

        {isFetching && (
          <Stack rowGap="1.5rem">
            {[1, 2, 3, 4].map((i) => (
              <Stack key={i} rowGap=".5rem">
                <Skeleton width="35%" height={16} />
                <Skeleton width="90%" height={14} />
                <Skeleton width="75%" height={14} />
              </Stack>
            ))}
          </Stack>
        )}

        {showError && (
          <EmptyStateCard
            image={noResultsImage}
            title="Couldn't load this check-in"
            body="This person may not be on your team, or the link may be out of date."
          />
        )}

        {showEmpty && (
          <EmptyStateCard
            image={noResultsImage}
            title="No check-in completed"
            body="No check-in was completed for this cycle."
          />
        )}

        {showContent && (
          <>
            <ModalSection title={null}>
              {getEmployeeSummaryPrepCard()}
            </ModalSection>

            <ModalSection title="Last Week">
              <Stack
                direction="row"
                justifyContent="space-between"
                columnGap="1rem"
                paddingY="1rem"
                paddingBottom="0"
              >
                {getPreviousCheckInSentimentSection()}
              </Stack>
            </ModalSection>
            {getWeekFocusSection()}
            {getNextWeekSection()}
            {!!checkInData.supportNeeds && (
              <ModalSection title={null}>
                <PrepSupportNeedCard content={checkInData.supportNeeds} />
              </ModalSection>
            )}
            {getConversationStarterSection()}
            {getPerformanceInANutshellCard()}
          </>
        )}
      </Stack>
    </InformationDrawerLayout>
  );
};

const ModalSection = ({
  title,
  children,
}: Readonly<{
  title: string | null;
  children: ReactNode | ReactNode[];
}>) => (
  <Box>
    {title && (
      <Box marginBottom="1rem">
        <Typography
          variant="cardBodyHeader"
          className="text-navy-100"
          sx={{ fontSize: "1rem", fontWeight: 600 }}
        >
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
);
